import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, PAGE_SIZE } from "@/lib/newsUtils";

export function useArticleFeed() {
  const [articles, setArticles] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedSource, setSelectedSource] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const cursorRef = useRef(null);

  const fetchSources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/sources`);
      setSources(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchArticles = useCallback(
    async (reset = false) => {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      try {
        const params = {
          limit: PAGE_SIZE,
          source: selectedSource,
        };
        if (!reset && cursorRef.current) params.cursor = cursorRef.current;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (search) params.search = search;
        const res = await axios.get(`${API}/articles`, { params });
        const { items, next_cursor } = res.data;
        setArticles((prev) => (reset ? items : [...prev, ...items]));
        cursorRef.current = next_cursor;
        setHasMore(Boolean(next_cursor));
      } catch (e) {
        console.error(e);
        toast.error("No se pudieron cargar las noticias.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [selectedSource, dateFrom, dateTo, search]
  );

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    cursorRef.current = null;
    setHasMore(true);
    fetchArticles(true);
  }, [selectedSource, dateFrom, dateTo, search, fetchArticles]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/refresh`);
      toast.success("Actualizando fuentes. Las nuevas noticias aparecerán en breve.");
      setTimeout(() => {
        cursorRef.current = null;
        setHasMore(true);
        fetchArticles(true);
        setRefreshing(false);
      }, 8000);
    } catch (e) {
      toast.error("Error al actualizar.");
      setRefreshing(false);
    }
  }, [fetchArticles]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading && !loadingMore) fetchArticles(false);
  }, [hasMore, loading, loadingMore, fetchArticles]);

  const clearFilters = useCallback(() => {
    setSelectedSource("all");
    setDateFrom("");
    setDateTo("");
    setSearchInput("");
    setSearch("");
  }, []);

  const hasActiveFilters =
    selectedSource !== "all" ||
    dateFrom !== "" ||
    dateTo !== "" ||
    search !== "";

  return {
    articles,
    sources,
    loading,
    loadingMore,
    hasMore,
    refreshing,
    refresh,
    loadMore,
    fetchSources,
    clearFilters,
    hasActiveFilters,
    filters: { selectedSource, dateFrom, dateTo, searchInput },
    set: {
      setSelectedSource,
      setDateFrom,
      setDateTo,
      setSearchInput,
    },
  };
}
